/**
 * AdminListFooter — footer condiviso per liste admin paginate.
 *
 * Mostra:
 *  - "X di Y entità" se totalCount è disponibile
 *  - "X entità mostrate" se totalCount missing
 *  - "X di Y mostrate (filtrate)" se inline-filter (loadedCount < filteredCount)
 *  - "Carica altre" button se hasMore
 *
 * Estratto da AdminCustomersPage 27/05/26 per riuso su tutte le tabelle
 * admin (Applications, Postings, Subscriptions, Users, AdminActions, ecc.).
 */
import { Loader2 } from 'lucide-react';

import { Button } from '@/components/ui/button';

export interface AdminListFooterProps {
  /** Numero di righe caricate dal BE (somma di tutte le pagine fetched). */
  loadedCount: number;
  /** Numero di righe attualmente visibili (dopo filter inline FE). Optional. */
  filteredCount?: number;
  /** Total count dal BE (Firestore count() aggregation). Optional — se missing mostra solo loaded. */
  totalCount?: number;
  /** Label entità — usato per pluralizzazione corretta (es. "azienda", "utente", "candidatura"). */
  entityLabel: string;
  /** Label entità plurale (es. "aziende", "utenti", "candidature"). */
  entityLabelPlural: string;
  /** True se c'è almeno una pagina successiva. */
  hasMore: boolean;
  /** Loading state load more button. */
  loadingMore: boolean;
  /** Callback per "Carica altre" button. */
  onLoadMore: () => void;
}

export function AdminListFooter({
  loadedCount,
  filteredCount,
  totalCount,
  entityLabel,
  entityLabelPlural,
  hasMore,
  loadingMore,
  onLoadMore,
}: AdminListFooterProps) {
  const effectiveFiltered = filteredCount ?? loadedCount;
  const isFiltered = filteredCount !== undefined && filteredCount < loadedCount;

  return (
    <div className="border-t border-border/50 p-3 text-center text-xs text-muted-foreground">
      {/* Counter display: 4 varianti */}
      {totalCount !== undefined ? (
        // Caso ideale: BE restituisce totalCount filtrato
        isFiltered ? (
          <>
            {effectiveFiltered} di {loadedCount} caricate · <strong>{totalCount}</strong> totali
          </>
        ) : (
          <>
            {loadedCount} di <strong>{totalCount}</strong> {totalCount === 1 ? entityLabel : entityLabelPlural}
          </>
        )
      ) : (
        // Fallback: totalCount missing, mostriamo solo loaded
        isFiltered ? (
          <>{effectiveFiltered} di {loadedCount} mostrate (filtrate)</>
        ) : (
          <>{loadedCount} {loadedCount === 1 ? entityLabel : entityLabelPlural}</>
        )
      )}

      {hasMore && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onLoadMore}
          disabled={loadingMore}
          className="ml-3"
        >
          {loadingMore && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
          Carica altre
        </Button>
      )}
    </div>
  );
}
